/**
 * Toile pack data — shared types, ratio/tagline/chips helpers, and the two wall/render helpers
 * (`packCells`, `packRatio`). The 82-pack `PACKS` array that used to live here (18 benched verticals +
 * wave 1's 64) was split into one `<slug>.ts` module per pack — narcis file-split shape, plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track A, script
 * `generation/scripts/packs/toile_split_data.py`. `registry.ts` assembles the modules back into
 * `packs`/`livePacks`; every pack here is still a faceless showcase: nine already-rendered cells
 * (3 readings x 3 styles), no generation wired. Samples live at
 * `pack-samples/toile/<slug>/` in the shared S3 bucket, served through the toile.pictures
 * CloudFront distribution — same shape as narcis's and ovid's own pack-samples prefixes.
 *
 * There is no DB-first prompt store here (`images.pack_prompts`, the `/pack` process's canonical home) —
 * that store exists to serve a generation request its render prompt at request time. Toile generates
 * nothing yet, so the recipe text below is display copy, not a render contract; it lives in code, the
 * layer that is enough for a fact with no guarantee to keep.
 */

export type PackCell = {
    /** file under /pack-samples/toile/<slug>/ */
    image: string
    subject: string
    style: string
    palette: string
    framing: string
}

export type PackReading = {
    key: string
    label: string
    subject: string
}

export type PackStyleCol = {
    key: string
    label: string
    style: string
    palette: string
    framing: string
    /** wallpaper only — the format changes the frame's aspect ratio per column */
    ratio?: {w: number; h: number}
}

/**
 * SEO backbone for a pack landing page (Track A item 2). Optional — a pack with no `seo` renders
 * today's byte-identical output (hardcoded FAQ, no intro/use-cases block). Populated by Track B's
 * `wave-promote --product toile --seo-copy` for future waves; none of the 82 migrated packs carry it
 * yet.
 */
export type ToilePackSeo = {
    title: string
    metaDescription: string
    h1: string
    tagline: string
    intro: string
    useCases: string[]
    faq: {q: string; a: string}[]
}

export type ToilePack = {
    slug: string
    /** short internal tag, doubles as the hub category and the eyebrow tail */
    category: string
    name: string
    tagline: string
    chips: string[]
    ratio: {w: number; h: number}
    readings: [PackReading, PackReading, PackReading]
    styles: [PackStyleCol, PackStyleCol, PackStyleCol]
    /** wall plan e's direction chips — a *direction*, not an append; may twist the subject entirely */
    directions?: [string, string, string]
    /** the example the empty direction field shows (`authoring_system_toile.md` § Directions), e.g. "try: a fox instead" */
    directionHint?: string
    seo?: ToilePackSeo
    /**
     * The cell (0-8, row-major = reading*3+style) that leads every first-glance surface — hub-card
     * thumb, wall cover, page hero (`.claude/skills/pack/SKILL.md` § The cover cell). Absent = 0
     * (`packCells()[0]`, today's default). Added Track B (`thinking/plans/2026_08_31_toile_pack_wiring.md`)
     * — no pack carries a non-zero value yet.
     */
    coverPosition?: number
}

export const A4 = {w: 832, h: 1216}
export const PORTRAIT = {w: 832, h: 1216}
export const SQUARE = {w: 1024, h: 1024}
export const LANDSCAPE = {w: 1216, h: 832}
export const WIDE = {w: 1344, h: 768}

export function tagline(items: string, readingsNoun: string, stylesNoun: string): string {
    return `Nine ${items}, three ${readingsNoun}, three ${stylesNoun} each.`
}

export function chips(readingsNoun: string, stylesNoun: string): string[] {
    return [`9 samples`, `3 ${readingsNoun} × 3 ${stylesNoun}`, `sample only`]
}

export function packCells(pack: ToilePack): PackCell[] {
    const out: PackCell[] = []
    for (const reading of pack.readings) {
        for (const style of pack.styles) {
            out.push({
                image: `${reading.key}-${style.key}.webp`,
                subject: reading.subject,
                style: style.style,
                palette: style.palette,
                framing: style.framing,
            })
        }
    }
    return out
}

export function packRatio(pack: ToilePack, styleKey?: string): {w: number; h: number} {
    if (styleKey) {
        const style = pack.styles.find((s) => s.key === styleKey)
        if (style?.ratio) return style.ratio
    }
    return pack.ratio
}
